import React from "react";
import Layout from "../../components/layout/layout";
import SeoComp from "../../components/seo/seo";
import { THIRD_PARTY_NOTICES_MD } from "../../config/thirdPartyNotices";

/**
 * Open-source licences (LIC-4).
 *
 * The licensing audit found zero copyleft on this site but also zero shipped
 * attribution — MIT/BSD/Apache require notice retention in a distribution,
 * and the five @fontsource families served from this domain are OFL-1.1,
 * which asks that the licence text stay available wherever the fonts go.
 * This page is that attribution.
 *
 * `src/config/thirdPartyNotices.js` is the audit's THIRD_PARTY_NOTICES-
 * website.md emitted VERBATIM as a JS constant — the legal deliverable.
 * Regenerate it when dependencies change; never hand-edit either file.
 *
 * Deliberately NOT a document in `src/content/legal/`: that pool is the
 * versioned customer-legal system (docType + current + manifest sync guard),
 * and a generated dependency list does not belong in it — one loose query
 * away from a legal page is exactly where the audit's orphaned-markdown
 * cleanup started. A tiny line classifier over the constant needs no
 * markdown dependency, which would itself join the list this page discloses.
 */

const parseNoticeLines = (md) => {
  const blocks = [];
  for (const raw of md.split("\n")) {
    const line = raw.trimEnd();
    if (!line.trim()) continue;
    if (/^\|[\s|:-]+\|$/.test(line)) continue; // table separator — layout only
    if (line.startsWith("# ")) blocks.push({ type: "title", text: line.slice(2) });
    else if (line.startsWith("## ")) blocks.push({ type: "heading", text: line.slice(3) });
    else if (line.startsWith("- ")) blocks.push({ type: "item", text: line.slice(2) });
    else if (line.startsWith("|")) blocks.push({ type: "table", text: line });
    else if (/^-{3,}$/.test(line)) blocks.push({ type: "divider" });
    else blocks.push({ type: "body", text: line });
  }
  return blocks;
};

/** Group consecutive item/table lines so they render as one list / one block. */
const groupBlocks = (blocks) => {
  const groups = [];
  for (const block of blocks) {
    const last = groups[groups.length - 1];
    if (last && last.type === block.type && (block.type === "item" || block.type === "table")) {
      last.lines.push(block.text);
    } else {
      groups.push({ type: block.type, lines: block.text != null ? [block.text] : [] });
    }
  }
  return groups;
};

const OpenSourceLicences = () => {
  const groups = groupBlocks(parseNoticeLines(THIRD_PARTY_NOTICES_MD));
  return (
    <Layout>
      <div className="legal-page">
        <article className="legal-document">
          <div className="legal-document__content">
            {groups.map((group, index) => {
              switch (group.type) {
                case "title":
                  return <h1 key={index}>{group.lines[0]}</h1>;
                case "heading":
                  return <h2 key={index}>{group.lines[0]}</h2>;
                case "item":
                  return (
                    <ul key={index}>
                      {group.lines.map((line, i) => (
                        <li key={i}>{line}</li>
                      ))}
                    </ul>
                  );
                case "table":
                  // The icon-font appendix table, verbatim. A <pre> keeps the
                  // pipe alignment readable without a table-markup dependency.
                  return (
                    <pre key={index} style={{ overflowX: "auto" }}>
                      {group.lines.join("\n")}
                    </pre>
                  );
                case "divider":
                  return <hr key={index} />;
                default:
                  return <p key={index}>{group.lines[0]}</p>;
              }
            })}
          </div>
        </article>
      </div>
    </Layout>
  );
};

export const Head = () => (
  <SeoComp
    title="Open-Source Licences | Duffloo"
    description="Third-party open-source software and font licences used by duffloo.com, with the attribution their licences require."
    pathname="/open-source-licences/"
  />
);

export default OpenSourceLicences;
